import React, { useState, useRef, useEffect } from 'react';
import Tooltip from './Tooltip';
import colors from '../../utils/colors';
import typography from '../../utils/typography';
import { TriangleDownIcon, SuccessIcon } from '../../utils/icons';

const SmallButton = ({ 
  onClick, 
  className = "", 
  variant = "primary", 
  label,
  leftIcon,
  rightIcon,
  tooltip,
  tooltipPosition = "top",
  tooltipDelay = 500,
  // Dropdown props
  dropdownOptions = null, // Array of { value, label, icon?, disabled? }
  dropdownValue = null,
  onDropdownChange = null,
  dropdownPosition = "bottom-right",
  dropdownWidth = 180,
  showDropdownCheck = true,
  showSelectedLabel = false // When true, shows selected option's label instead of custom label
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const tooltipTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  
  const isDropdown = Boolean(dropdownOptions && dropdownOptions.length > 0);
  const selectedOption = dropdownOptions?.find(opt => opt.value === dropdownValue);
  
  const getButtonStyles = () => {
    switch (variant) {
      case "primary":
        return `hover:bg-opacity-90`;
      case "secondary":
        return `hover:bg-opacity-80`;
      case "tertiary":
        return `bg-transparent border hover:bg-white/5`;
      case "aura":
        return `hover:brightness-110`;
      default:
        return `hover:bg-opacity-90`;
    }
  };

  // Determine button sizing based on content
  const hasLabel = Boolean(label);
  
  // Min size is 32x32, expand horizontally if needed for content
  const buttonSize = hasLabel ? "min-w-8 min-h-8 h-8 px-3 gap-1" : "min-w-8 min-h-8 w-8 h-8";

  const getBackgroundStyle = () => {
    const baseStyle = {};
    
    if (variant === "aura") {
      baseStyle.backgroundImage = "url('/aura.png')";
      baseStyle.backgroundSize = "cover";
      baseStyle.backgroundPosition = "center";
      baseStyle.backgroundRepeat = "no-repeat";
    }
    
    if (variant === "primary") {
      baseStyle.backgroundColor = colors.text.primary;
    }

    if (variant === "secondary") {
      baseStyle.backgroundColor = colors.bg.fog_thin;
    }

    if (variant === "tertiary") {
      baseStyle.borderColor = colors.border.primary;
    }
    
    return baseStyle;
  };

  const getTextColor = () => {
    switch (variant) {
      case "primary":
        return colors.bg.primary || '#0E0808';
      case "secondary":
      case "tertiary":
      case "aura":
        return colors.text.primary || '#FAF7F5';
      default:
        return colors.bg.primary || '#0E0808';
    }
  };

  const getIconColor = () => {
    switch (variant) {
      case "primary":
        return colors.bg.primary || '#0E0808';
      case "secondary":
      case "tertiary":
      case "aura":
        return colors.text.primary || '#FAF7F5';
      default:
        return colors.bg.primary || '#0E0808';
    }
  };

  const handleMouseEnter = () => {
    if (tooltip) {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
      tooltipTimeoutRef.current = setTimeout(() => {
        setShowTooltip(true);
      }, tooltipDelay);
    }
  };

  const handleMouseLeave = () => {
    if (tooltip) {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
      setShowTooltip(false);
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isDropdownOpen]);

  const handleButtonClick = (e) => {
    if (isDropdown) {
      e.preventDefault();
      setIsDropdownOpen(!isDropdownOpen);
    } else if (onClick) {
      onClick(e);
    }
  };

  const handleDropdownOptionSelect = (optionValue) => {
    if (onDropdownChange) {
      onDropdownChange(optionValue);
    }
    setIsDropdownOpen(false);
  };

  const getDropdownPosition = () => {
    const positions = {
      "bottom-left": "top-full left-0 mt-1",
      "bottom-right": "top-full right-0 mt-1", 
      "top-left": "bottom-full left-0 mb-1",
      "top-right": "bottom-full right-0 mb-1"
    };
    return positions[dropdownPosition] || positions["bottom-right"];
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`flex justify-center items-center flex-grow-0 flex-shrink-0 rounded-[100px] cursor-pointer transition-all ${buttonSize} ${getButtonStyles()} ${className}`}
        style={getBackgroundStyle()}
        onClick={handleButtonClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Left Icon - User controlled or selected option fallback */}
        {leftIcon ? (
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0" style={{ color: getIconColor() }}>
            {leftIcon}
          </div>
        ) : isDropdown && selectedOption?.icon && !label && !rightIcon ? (
          // Fallback: show selected option icon only if no other content
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0" style={{ color: getIconColor() }}>
            {selectedOption.icon}
          </div>
        ) : null}
        
        {/* Label - User controlled, selected option, or fallback */}
        {label && showSelectedLabel && isDropdown && selectedOption ? (
          // Show selected option's label when showSelectedLabel is true
          <span className="whitespace-nowrap" style={{ ...typography.xsmall.title, color: getTextColor() }}>
            {selectedOption.label}
          </span>
        ) : label ? (
          // Show custom label
          <span className="whitespace-nowrap" style={{ ...typography.xsmall.title, color: getTextColor() }}>
            {label}
          </span>
        ) : isDropdown && selectedOption && !leftIcon && !rightIcon ? (
          // Fallback: show selected option label only if no other content
          <span className="whitespace-nowrap" style={{ ...typography.xsmall.title, color: getTextColor() }}>
            {selectedOption.label}
          </span>
        ) : null}
        
        {/* Right Icon - User controlled or dropdown arrow */}
        {rightIcon ? (
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0" style={{ color: getIconColor() }}>
            {rightIcon}
          </div>
        ) : isDropdown ? (
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0" style={{ color: getIconColor() }}>
            <TriangleDownIcon />
          </div>
        ) : null}
      </div>

      {/* Dropdown Menu */}
      {isDropdown && isDropdownOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsDropdownOpen(false)}
          />
          
          {/* Options */}
          <div 
            className={`absolute z-50 ${getDropdownPosition()} rounded-lg bg-[#363030] overflow-hidden`}
            style={{ 
              boxShadow: "0px 1px 16px 0 rgba(0,0,0,0.4)",
              width: typeof dropdownWidth === "number" ? `${dropdownWidth}px` : dropdownWidth
            }}
          >
            <div className="flex flex-col">
              {dropdownOptions.map((option, index) => {
                const isSelected = option.value === dropdownValue;
                const isDisabled = option.disabled;
                
                return (
                  <div
                    key={option.value || index}
                    className={`flex items-center h-10 px-3 py-[13px] gap-2 transition-colors overflow-hidden ${
                      isDisabled
                        ? 'opacity-50 cursor-not-allowed'
                        : isSelected
                        ? 'bg-white/[0.04] hover:bg-white/[0.06] cursor-pointer'
                        : 'bg-[#363030] hover:bg-white/[0.02] cursor-pointer'
                    }`}
                    onClick={() => !isDisabled && handleDropdownOptionSelect(option.value)}
                  >
                    {/* Option Icon */}
                    {option.icon && (
                      <div className="flex-shrink-0 w-4 h-4">
                        {option.icon}
                      </div>
                    )}
                    
                    {/* Option Label */}
                    <p className="text-sm font-medium text-left text-white flex-1 truncate">
                      {option.label}
                    </p>
                    
                    {/* Check Icon */}
                    {showDropdownCheck && isSelected && !isDisabled && (
                      <div className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
                        <SuccessIcon />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {tooltip && (
        <Tooltip 
          text={tooltip} 
          isVisible={showTooltip} 
          position={tooltipPosition} 
        />
      )}
    </div>
  );
};

export default SmallButton;